<script setup>
const props = defineProps({
  name: {
    type: String,
    default: undefined,
  },
  text: {
    type: String,
    default: undefined,
  },

  theme: {
    type: String,
    default: 'gray-purple',
    validator(theme) {
      return ['gray-purple', 'purple-orange'].includes(theme);
    },
  },
  mode: {
    type: String,
    default: 'circle',
    validator(mode) {
      return ['circle', 'check'].includes(mode);
    },
  },

  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },

  validation: {
    type: [String, Object],
    default: undefined,
  },
});

const emit = defineEmits(['update:modelValue']);

const localValue = useVModel(props, 'modelValue', emit);
</script>

<template>
  <label
    class="form-field-checkbox"
    :class="[
      `form-field-checkbox--theme--${theme}`,
      { 'form-field-checkbox--disabled': disabled },
    ]"
    @click.stop
  >
    <VeeField
      v-slot="{ field, errors }"
      v-model="localValue"
      :name="name"
      :rules="validation"
      type="checkbox"
      :value="true"
      :unchecked-value="false"
      :validate-on-model-update="false"
    >
      <input
        v-bind="field"
        class="form-field-checkbox__field"
        :class="`form-field-checkbox__field--mode--${mode}`"
        type="checkbox"
        :name="name"
        :disabled="disabled"
      />

      <span
        class="form-field-checkbox__fake-field"
        :class="[
          `form-field-checkbox__fake-field--mode--${mode}`,
          { 'form-field-checkbox__fake-field--invalid': errors.length },
        ]"
      />

      <slot>
        <span v-if="text" class="form-field-checkbox__font">
          {{ text }}
        </span>
      </slot>

      <slot name="content" :errors="errors" />
    </VeeField>
  </label>
</template>

<style scoped lang="scss">
.form-field-checkbox {
  $parent: &;

  display: flex;
  align-items: center;
  cursor: pointer;

  &__font {
    font-size: em(17);
    font-weight: 400;
    line-height: math.div(24, 17);
  }

  &__field {
    display: none;

    &--mode {
      &--circle {
        &:checked + span {
          &::after {
            left: em(23);
          }
        }
      }
    }
  }

  &__fake-field {
    position: relative;
    flex-shrink: 0;
    transition:
      background-color $time-3,
      border-color $time-3;

    &:not(:last-child) {
      margin-right: em(12);
    }

    &::after {
      position: absolute;
      top: 50%;
    }

    &--mode {
      &--circle {
        width: em(51);
        height: em(30);
        border-radius: em(15);

        &::after {
          @include box(em(26));

          left: em(2);
          content: '';
          background-color: $color-white;
          border-radius: 50%;
          transition: left $time-3;
          transform: translateY(-50%);
        }
      }

      &--check {
        @include box(em(20));

        border: 1px solid $color-gray-8;
        border-radius: em(4);

        &::after {
          left: 50%;
          font-size: em(14);
          color: $color-white;
          content: '\2713';
          transform: translate(-50%, -50%);
        }
      }
    }

    &--invalid {
      border-color: $color-error;
    }
  }

  &--disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &--theme {
    &--gray-purple {
      #{$parent} {
        &__field {
          &--mode {
            &--circle {
              &:checked + span {
                background-color: $color-main-1;
              }
            }

            &--check {
              &:checked + span {
                background-color: $color-main-1;
                border-color: $color-main-1;
              }
            }
          }
        }

        &__fake-field {
          &--mode {
            &--circle {
              background-color: $color-gray-8;
            }

            &--check {
              background-color: $color-white;
            }
          }
        }
      }
    }

    &--purple-orange {
      #{$parent} {
        &__field {
          &--mode {
            &--circle {
              &:checked + span {
                background-color: $color-second-1;
              }
            }
          }
        }

        &__fake-field {
          &--mode {
            &--circle {
              background-color: $color-main-14;
            }
          }
        }
      }
    }
  }
}
</style>
